// # Calendar component for dedicated tasks
import React, { useState, useCallback, useEffect, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import { Checkbox, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';  // # Import PropTypes
import './give_dedic1_cld.css';

// # Define task contexts (copied from main file for calendar coloring)
const TASK_CONTEXTS = {
  general: { label: 'General', color: '#3498db' },
  study: { label: 'Study', color: '#2ecc71' },
  practice: { label: 'Practice', color: '#f39c12' }
};

// # Define calendar colors
const CALENDAR_COLORS = {
  unchecked: '#f8d7da', // # Soft rose/pink - professional but noticeable
  checked: '#d4edda',   // # Soft mint green - calming and professional
  general: '#e2f0fd',   // # Soft blue - for general context
  study: '#e8f5e9',     // # Soft green - for study context
  practice: '#fff3e0'   // # Soft orange - for practice context
};

function DedicatedCalendar({ tasks, onToggleComplete, onViewTask, onUpdateDate, isMentor = false, isDarkMode = false }) {
  const calendarRef = useRef(null);
  
  // # State for drag tracking
  const [dragState, setDragState] = useState({
    isDragging: false,
    draggedTaskId: null
  });
  
  // # State for animation
  const [animatedCells, setAnimatedCells] = useState([]);

  // # Clear animation after it plays
  useEffect(() => {
    if (animatedCells.length > 0) {
      const timer = setTimeout(() => {
        setAnimatedCells([]);
      }, 800); // Slightly longer than animation duration
      return () => clearTimeout(timer);
    }
  }, [animatedCells]);

  // # Convert tasks object to array for calendar events
  const calendarEvents = Object.entries(tasks).map(([id, task]) => ({
    id,
    title: task.title,
    start: task.date,
    allDay: true,
    backgroundColor: 'transparent', // # Remove default background
    textColor: '#333333',
    extendedProps: {
      description: task.description,
      context: task.context,
      chapter: task.chapter,
      qbank: task.qbank,
      completed: task.completed
    },
    // # Make events draggable only for mentors
    editable: isMentor,
    durationEditable: false
  }));

  // # Memoize handlers for better performance
  const handleDragStart = useCallback((e, taskId) => {
    if (!isMentor) return; // # Only allow mentors to drag
    setDragState({
      isDragging: true,
      draggedTaskId: taskId
    });
    // # Add data to drag event
    e.dataTransfer.setData('text/plain', taskId);
    // # Set effectAllowed for better performance
    e.dataTransfer.effectAllowed = 'move';
  }, [isMentor]);

  const handleDragEnd = useCallback(() => {
    setDragState({
      isDragging: false,
      draggedTaskId: null
    });
  }, []);

  // # Handle event render
  const handleEventContent = (eventInfo) => {
    const { event } = eventInfo;
    const isCompleted = event.extendedProps.completed || false;
    const context = event.extendedProps.context || 'general';
    
    // # Determine background color based on completion status and context
    let bgColor = isCompleted ? CALENDAR_COLORS.checked : CALENDAR_COLORS.unchecked;
    
    return (
      <Tooltip title={`${event.title} - ${context.charAt(0).toUpperCase() + context.slice(1)}`} arrow placement="top">
        <div 
          className={`gv_ddc1_cld_event ${dragState.isDragging && dragState.draggedTaskId === event.id ? 'gv_ddc1_cld_dragging' : ''} ${isCompleted ? 'gv_ddc1_cld_completed' : ''} ${isMentor ? 'gv_ddc1_cld_mentor' : ''}`}
          style={{
            backgroundColor: bgColor
          }}
          draggable={isMentor}
          onDragStart={(e) => handleDragStart(e, event.id)}
          onDragEnd={handleDragEnd}
        >
          <div 
            className="gv_ddc1_cld_checkbox_wrapper"
            onClick={(e) => {
              e.stopPropagation(); // # Prevent event click/double-click
            }}
          >
            <Checkbox
              checked={isCompleted}
              onChange={(e) => {
                e.stopPropagation(); // # Prevent event click/double-click
                onToggleComplete(event.id);
              }}
              className="gv_ddc1_cld_checkbox"
              size="small"
              disabled={!isMentor} // # Only mentors can toggle completion
            />
          </div>
          <div 
            className="gv_ddc1_cld_content_wrapper"
            onDoubleClick={(e) => {
              e.stopPropagation();
              const task = tasks[event.id];
              if (task) {
                onViewTask({
                  id: event.id,
                  ...task
                });
              }
            }}
          >
            <span className="gv_ddc1_cld_event_title">{event.title}</span>
          </div>
        </div>
      </Tooltip>
    );
  };

  // # Handle event drop (date change)
  const handleEventDrop = useCallback((info) => {
    if (!isMentor) return; // # Only allow mentors to update dates
    const { event } = info;
    const taskId = event.id;
    const newDate = event.startStr;
    
    // # Add cell to animated cells
    setAnimatedCells([newDate]);
    
    // # Call the parent component's update function
    onUpdateDate(taskId, newDate);
  }, [onUpdateDate, isMentor]);

  // # Handle date click for dropping
  const handleDateClick = useCallback((info) => {
    if (!isMentor) return; // # Only allow mentors to update dates
    if (dragState.isDragging && dragState.draggedTaskId) {
      const taskId = dragState.draggedTaskId;
      const newDate = info.dateStr;
      
      // # Add cell to animated cells
      setAnimatedCells([newDate]);
      
      // # Call the parent component's update function
      onUpdateDate(taskId, newDate);
      
      // # Reset drag state
      setDragState({
        isDragging: false,
        draggedTaskId: null
      });
    }
  }, [dragState, onUpdateDate, isMentor]);
  
  // # Handle day cell render to add animation
  const handleDayCellDidMount = useCallback((info) => {
    if (animatedCells.includes(info.date.toISOString().split('T')[0])) {
      info.el.classList.add('gv_ddc1_cld_drop_success');
    }
  }, [animatedCells]);

  return (
    <div className={`gv_ddc1_cld_section ${isMentor ? 'gv_ddc1_cld_mentor_mode' : ''} ${isDarkMode ? 'gv_ddc1_dark-mode' : ''}`}>
      <div className="gv_ddc1_cld_content">
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,listWeek'
          }}
          events={calendarEvents}
          height="auto"
          aspectRatio={1.8}
          eventContent={handleEventContent}
          eventClassNames="gv_ddc1_cld_event_wrapper"
          editable={isMentor}
          droppable={isMentor}
          eventDrop={handleEventDrop}
          dateClick={handleDateClick}
          eventDragMinDistance={5} // # Reduce drag threshold for faster response
          dragRevertDuration={0} // # No revert animation for faster response
          dayCellDidMount={handleDayCellDidMount}
          dayMaxEventRows={false} // # Allow unlimited rows of events
          firstDay={1} // # Start week on Monday
          nowIndicator={true} // # Show current time indicator
          weekNumbers={true} // # Show week numbers
          weekNumberFormat={{ week: 'numeric' }}
          weekText="W"
        />
      </div>
    </div>
  );
}

DedicatedCalendar.propTypes = {
  tasks: PropTypes.object.isRequired,
  onToggleComplete: PropTypes.func.isRequired,
  onViewTask: PropTypes.func.isRequired,
  onUpdateDate: PropTypes.func.isRequired,
  isMentor: PropTypes.bool,
  isDarkMode: PropTypes.bool
};

export default DedicatedCalendar;
